import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { Atmos, Chip, Field, GlassCard, GoldButton } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { consumeKrnosView } from '../olmps/links/krnosView';
import { useOnboarding, useTourTarget, tourBus } from '../v10/onboarding';
import { ScreenHelp } from '../v10/ScreenHelp';
import { krnosHelp } from '../olmps/help/krnosHelp';

// ---------------------------------------------------------------------------
// KRNOS Schedule — native replica of the web week/day time-grid calendar
// (src/app/calendar/page.tsx). See the discussion log entry for the full
// feature/exclusion breakdown.
// ---------------------------------------------------------------------------

type RepeatOption = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
type ViewMode = 'weekly' | 'daily' | 'monthly';
type DeleteScope = 'nonRecurring' | 'single' | 'future';

export interface EventRow {
  id: string;
  user_id: string;
  title: string;
  date: string; // yyyy-MM-dd
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  notes: string | null;
  color: string | null;
  repeat: RepeatOption | null;
  end_date: string | null;
  exceptions: string[] | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OverlapLayout {
  columnIndex: number;
  totalColumns: number;
}

// --- Calendar-display window (Settings > Calendar Configuration). These are
// fallback defaults matching the web app's defaults; the actual per-user
// values are loaded from user_settings.calendar_page on mount (see the
// calendarConfig state in KrnosTool below) and fall back to these when the
// row/column is absent or malformed. ---
const DAY_START_TIME = '03:00';
const DAY_END_TIME = '20:00';
const INTERVAL_MINUTES = 30;
const SLOT_HEIGHT = 26;
const TIME_COL_WIDTH = 54;
const DAY_COL_WIDTH = 104;

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DEFAULT_EVENT_COLOR = '#8b5cf6';
// Same fixed 8-swatch palette the web app uses for its "Calendars" colors
// (calendarColorOptions in page.tsx), used here in lieu of the web's
// react-colorful hex picker (no new deps allowed on mobile).
const COLOR_SWATCHES = [
  '#3b82f6', // blue (bg-primary)
  '#22c55e', // green-500
  '#ef4444', // red-500
  '#eab308', // yellow-500
  '#a855f7', // purple-500
  '#ec4899', // pink-500
  '#f97316', // orange-500
  '#06b6d4', // cyan-500
];
// Legacy Tailwind-class colors that pre-hex-picker web events may still have
// stored (see resolveItemColor in page.tsx / normalizeColorToHex in the
// event form dialog). Mapped to their approximate hex so older rows still
// render with the right color natively.
const LEGACY_CLASS_TO_HEX: Record<string, string> = {
  'bg-primary': '#3b82f6',
  'bg-secondary': '#9aa3b2',
  'bg-accent': '#f97316',
  'bg-destructive': '#ef4444',
  'bg-green-500': '#22c55e',
  'bg-yellow-500': '#eab308',
  'bg-purple-500': '#a855f7',
  'bg-purple-600': '#9333ea',
  'bg-pink-500': '#ec4899',
  'bg-accent/70': '#f97316',
};

const REPEAT_OPTIONS: RepeatOption[] = ['none', 'daily', 'weekly', 'monthly', 'yearly'];

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function parseDateKey(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

export function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}

export function startOfWeekSun(d: Date): Date {
  const copy = startOfDay(d);
  copy.setDate(copy.getDate() - copy.getDay());
  return copy;
}

function endOfWeekSun(d: Date): Date {
  const copy = startOfDay(d);
  copy.setDate(copy.getDate() + (6 - copy.getDay()));
  return copy;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 0, 0, 0, 0);
}

function addMonths(d: Date, n: number): Date {
  const copy = new Date(d);
  copy.setMonth(copy.getMonth() + n);
  return copy;
}

function isSameMonthDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function isSameDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isBeforeDate(a: Date, b: Date): boolean {
  const ka = a.getFullYear() * 10000 + (a.getMonth() + 1) * 100 + a.getDate();
  const kb = b.getFullYear() * 10000 + (b.getMonth() + 1) * 100 + b.getDate();
  return ka < kb;
}

export function parseTimeToMinutes(hhmm: string): number {
  if (!hhmm || !hhmm.includes(':')) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

export function minutesToTimeStr(totalMinutes: number): string {
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(wrapped / 60))}:${pad2(wrapped % 60)}`;
}

function normalizeTimeInput(text: string): string | null {
  const m = text.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (!m) return null;
  return `${pad2(Number(m[1]))}:${m[2]}`;
}

export function formatTimeDisplay(hhmm: string): string {
  const mins = parseTimeToMinutes(hhmm);
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const suffix = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${pad2(m)} ${suffix}`;
}

function formatFullDate(d: Date): string {
  return `${FULL_DAY_NAMES[d.getDay()]}, ${MONTH_NAMES_FULL[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatShortDate(d: Date): string {
  return `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function generateTimeSlots(startStr: string, endStr: string, interval: number): string[] {
  const slots: string[] = [];
  const start = parseTimeToMinutes(startStr);
  const end = parseTimeToMinutes(endStr);
  if (end <= start) return slots;
  for (let cur = start; cur < end; cur += interval) {
    slots.push(minutesToTimeStr(cur));
  }
  return slots;
}

export function resolveColorHex(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_EVENT_COLOR;
  if (raw.startsWith('#')) return raw;
  return LEGACY_CLASS_TO_HEX[raw] ?? DEFAULT_EVENT_COLOR;
}

// Mirrors isEventOccurringOnDate() in src/app/calendar/page.tsx (task branch
// omitted — mobile KRNOS only surfaces calendar_events rows).
export function isEventOccurringOnDate(item: EventRow, checkDate: Date): boolean {
  const eventStart = parseDateKey(item.date);

  if (item.exceptions && item.exceptions.includes(dateKey(checkDate))) return false;

  if (item.end_date) {
    const seriesEnd = parseDateKey(item.end_date);
    if (isBeforeDate(seriesEnd, checkDate)) return false;
  }

  if (isBeforeDate(checkDate, eventStart) && !isSameDate(checkDate, eventStart)) return false;

  if (!item.repeat || item.repeat === 'none') {
    return isSameDate(eventStart, checkDate);
  }

  if (isBeforeDate(checkDate, eventStart)) return false;

  if (item.repeat === 'daily') return true;
  if (item.repeat === 'weekly') return checkDate.getDay() === eventStart.getDay();
  if (item.repeat === 'monthly') return checkDate.getDate() === eventStart.getDate();
  if (item.repeat === 'yearly') {
    return checkDate.getMonth() === eventStart.getMonth() && checkDate.getDate() === eventStart.getDate();
  }
  return false;
}

// Column-assignment range: mirrors computeOverlapLayout()'s simple bump.
function overlapRange(item: EventRow, interval: number = INTERVAL_MINUTES): { start: number; end: number } {
  const start = parseTimeToMinutes(item.start_time);
  let end = parseTimeToMinutes(item.end_time);
  if (end <= start) end = start + interval;
  return { start, end };
}

// Render range: mirrors the more nuanced duration fallback used when
// actually drawing each event block in WeeklyViewComponent/DailyViewComponent.
export function renderRange(item: EventRow, interval: number = INTERVAL_MINUTES): { start: number; end: number } {
  const start = parseTimeToMinutes(item.start_time);
  const end = parseTimeToMinutes(item.end_time);
  let duration = end - start;
  if (duration <= 0 && item.end_time === '00:00') {
    duration = 24 * 60 - start;
  } else if (duration <= 0) {
    duration = end + 24 * 60 - start;
  }
  if (duration <= 0) duration = interval;
  return { start, end: start + duration };
}

export function computeOverlapLayout(dayItems: EventRow[], interval: number = INTERVAL_MINUTES): Map<string, OverlapLayout> {
  if (dayItems.length === 0) return new Map();
  const ranges = dayItems.map((item) => ({ id: item.id, ...overlapRange(item, interval) }));
  ranges.sort((a, b) => a.start - b.start || b.end - a.end);

  const columns: { end: number }[][] = [];
  const colOf = new Map<string, number>();
  for (const r of ranges) {
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      const col = columns[c];
      if (col[col.length - 1].end <= r.start) {
        col.push({ end: r.end });
        colOf.set(r.id, c);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([{ end: r.end }]);
      colOf.set(r.id, columns.length - 1);
    }
  }

  const result = new Map<string, OverlapLayout>();
  for (const r of ranges) {
    const overlapping = ranges.filter((o) => o.start < r.end && o.end > r.start);
    const totalColumns = Math.max(...overlapping.map((o) => (colOf.get(o.id) ?? 0) + 1));
    result.set(r.id, { columnIndex: colOf.get(r.id) ?? 0, totalColumns });
  }
  return result;
}

function repeatLabel(item: EventRow): string | null {
  if (!item.repeat || item.repeat === 'none') return null;
  const base = `Repeats ${item.repeat}`;
  if (item.end_date) return `${base} · until ${item.end_date}`;
  return base;
}

interface FormState {
  mode: 'add' | 'edit';
  id: string | null;
  original: EventRow | null;
  title: string;
  notes: string;
  date: Date;
  startTime: string;
  endTime: string;
  color: string;
  repeat: RepeatOption;
  endDate: Date | null;
}

/** Tour target wrapper for controls that take no ref. */
function TourBox({ id, children, style }: { id: string; children: React.ReactNode; style?: object }) {
  const tt = useTourTarget(id);
  return <View ref={tt.onLayoutRef} collapsable={false} style={style}>{children}</View>;
}

/** The add/edit form. A native Modal would sit above the tour overlay, so while the tour runs it renders inline. */
function FormShell({ inline, visible, onRequestClose, children }: { inline: boolean; visible: boolean; onRequestClose: () => void; children: React.ReactNode }) {
  if (inline) return visible ? <View style={[StyleSheet.absoluteFill, { zIndex: 10 }]}>{children}</View> : null;
  return <Modal visible={visible} transparent animationType="slide" onRequestClose={onRequestClose}>{children}</Modal>;
}

function blankForm(date: Date, startTime?: string, interval: number = INTERVAL_MINUTES): FormState {
  const start = startTime ?? '09:00';
  return {
    mode: 'add',
    id: null,
    original: null,
    title: '',
    notes: '',
    date,
    startTime: start,
    endTime: minutesToTimeStr(parseTimeToMinutes(start) + interval),
    color: DEFAULT_EVENT_COLOR,
    repeat: 'none',
    endDate: null,
  };
}

/**
 * The full KRNOS Schedule (week/day/month time-grid). Rendered standalone as
 * the KRNOS tool, and embedded inside TKNE's Calendar section scoped to one
 * calendar. When `calendarId` is set, events are loaded from and created in
 * that calendar only; when omitted, it's the user's whole calendar (KRNOS).
 * `embedded` swaps the flex-fill root for a fixed height so it works inside a
 * scrolling parent.
 */
export function KrnosSchedule({ calendarId, embedded = false }: { calendarId?: string; embedded?: boolean } = {}) {
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user.id;
  const { c } = useTheme();
  const styles = useStyles();
  const ob = useOnboarding();

  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The user's configured calendar display window (Settings > Calendar
  // Configuration on web, user_settings.calendar_page). Defaults to the
  // web's defaults until/unless the user's row says otherwise.
  const [calendarConfig, setCalendarConfig] = useState({
    dayStart: DAY_START_TIME,
    dayEnd: DAY_END_TIME,
    interval: INTERVAL_MINUTES,
  });

  const [currentDate, setCurrentDate] = useState<Date>(() => startOfDay(new Date()));
  const [view, setView] = useState<ViewMode>(() => consumeKrnosView() ?? 'weekly');
  const [containerWidth, setContainerWidth] = useState<number>(Dimensions.get('window').width);
  // "TSK" toggle: mirrors web's tskMode — when on, recurring events (repeat
  // set and not 'none') are hidden from the grid/month view entirely.
  const [tskMode, setTskMode] = useState(false);

  const [form, setForm] = useState<FormState | null>(null);
  const [detail, setDetail] = useState<{ item: EventRow; occurrenceDate: string } | null>(null);
  // Tour: a wait that is already satisfied when its step starts fires at once.
  useEffect(() => {
    if (!ob.active) return;
    if (ob.liveTarget === 'krnos.title' && form?.title.trim()) tourBus.emit('krnos:title');
    else if (ob.liveTarget === 'krnos.repeat.weekly' && form?.repeat === 'weekly') tourBus.emit('krnos:repeat:weekly');
  }, [ob.active, ob.liveTarget, form]);

  const bodyHScroll = useRef<ScrollView>(null);
  const headerHScroll = useRef<ScrollView>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    let query = supabase.from('calendar_events').select('*').eq('user_id', userId);
    if (calendarId) query = query.eq('calendar_id', calendarId);
    const { data, error: err } = await query;
    if (err) {
      setError(err.message);
    } else {
      setError(null);
      setEvents((data ?? []) as EventRow[]);
    }
    setLoading(false);
  }, [userId, calendarId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Read-only load of the user's calendar_page config. Parsed defensively —
  // any missing/malformed field falls back to the hardcoded defaults above,
  // and the whole row is ignored if dayEnd <= dayStart.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_settings')
        .select('calendar_page')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      const cfg = (data?.calendar_page ?? {}) as Partial<{
        dayStartTime: string;
        dayEndTime: string;
        timeIntervalMinutes: number;
      }>;
      const timeRe = /^([01]?\d|2[0-3]):[0-5]\d$/;
      const dayStart = typeof cfg.dayStartTime === 'string' && timeRe.test(cfg.dayStartTime)
        ? cfg.dayStartTime
        : DAY_START_TIME;
      const dayEnd = typeof cfg.dayEndTime === 'string' && timeRe.test(cfg.dayEndTime)
        ? cfg.dayEndTime
        : DAY_END_TIME;
      const interval = cfg.timeIntervalMinutes === 30 || cfg.timeIntervalMinutes === 60
        ? cfg.timeIntervalMinutes
        : INTERVAL_MINUTES;
      if (parseTimeToMinutes(dayEnd) > parseTimeToMinutes(dayStart)) {
        setCalendarConfig({ dayStart, dayEnd, interval });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const timeSlots = useMemo(
    () => generateTimeSlots(calendarConfig.dayStart, calendarConfig.dayEnd, calendarConfig.interval),
    [calendarConfig]
  );
  const dayStartMin = useMemo(() => parseTimeToMinutes(calendarConfig.dayStart), [calendarConfig]);
  const dayEndMin = useMemo(() => parseTimeToMinutes(calendarConfig.dayEnd), [calendarConfig]);
  const pxPerMinute = SLOT_HEIGHT / calendarConfig.interval;
  const gridHeight = timeSlots.length * SLOT_HEIGHT;

  const weekDays = useMemo(() => {
    const start = startOfWeekSun(currentDate);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  // Mirrors updateCalendarData()'s monthly branch: 7×N grid spanning the
  // Sunday-start week containing the 1st through the Saturday-end week
  // containing the last day of the month (typically 5-6 rows).
  const monthDays = useMemo(() => {
    if (view !== 'monthly') return [];
    const gridStart = startOfWeekSun(startOfMonth(currentDate));
    const gridEnd = endOfWeekSun(endOfMonth(currentDate));
    const days: Date[] = [];
    let cur = gridStart;
    while (!isBeforeDate(gridEnd, cur)) {
      days.push(cur);
      cur = addDays(cur, 1);
    }
    return days;
  }, [view, currentDate]);

  const daysToShow = view === 'weekly' ? weekDays : view === 'monthly' ? monthDays : [currentDate];

  // TSK toggle filter: mirrors filteredDisplayItems' `if (tskMode && item.repeat
  // && item.repeat !== 'none') return false;` — hides recurring events.
  const visibleEvents = useMemo(() => {
    if (!tskMode) return events;
    return events.filter((ev) => !ev.repeat || ev.repeat === 'none');
  }, [events, tskMode]);

  const dateRangeLabel = useMemo(() => {
    if (view === 'daily') return formatFullDate(currentDate);
    if (view === 'monthly') return `${MONTH_NAMES_FULL[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const start = weekDays[0];
    const end = weekDays[6];
    if (!start || !end) return '';
    const startMonth = MONTH_NAMES_FULL[start.getMonth()];
    const endMonth = MONTH_NAMES_FULL[end.getMonth()];
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
  }, [view, currentDate, weekDays]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    for (const day of daysToShow) {
      map.set(dateKey(day), visibleEvents.filter((ev) => isEventOccurringOnDate(ev, day)));
    }
    return map;
  }, [visibleEvents, daysToShow]);

  const goPrevious = () =>
    setCurrentDate((d) => (view === 'weekly' ? addDays(d, -7) : view === 'monthly' ? addMonths(d, -1) : addDays(d, -1)));
  const goNext = () =>
    setCurrentDate((d) => (view === 'weekly' ? addDays(d, 7) : view === 'monthly' ? addMonths(d, 1) : addDays(d, 1)));
  const goToday = () => setCurrentDate(startOfDay(new Date()));

  // Tap a day cell in month view → jump to daily view for that date, mirroring
  // web's openFormForNewEvent-on-cell-click UX adapted for mobile navigation
  // (native taps drill in rather than opening the add-event form directly).
  const openDayFromMonth = (day: Date) => {
    setCurrentDate(startOfDay(day));
    setView('daily');
  };

  const openAddForm = (day?: Date, startTime?: string) => {
    setForm(blankForm(day ?? currentDate, startTime, calendarConfig.interval));
  };

  const openEditForm = (item: EventRow, occurrenceDate: string) => {
    setForm({
      mode: 'edit',
      id: item.id,
      original: item,
      title: item.title,
      notes: item.notes ?? '',
      date: parseDateKey(occurrenceDate),
      startTime: item.start_time,
      endTime: item.end_time,
      color: resolveColorHex(item.color),
      repeat: (item.repeat as RepeatOption) ?? 'none',
      endDate: item.end_date ? parseDateKey(item.end_date) : null,
    });
    setDetail(null);
  };

  const closeForm = () => setForm(null);

  const insertNewEvent = async () => {
    if (!form || !userId) return;
    setBusy(true);
    const now = new Date().toISOString();
    const { error: err } = await supabase.from('calendar_events').insert({
      user_id: userId,
      calendar_id: calendarId ?? null,
      title: form.title.trim(),
      date: dateKey(form.date),
      start_time: form.startTime,
      end_time: form.endTime,
      notes: form.notes.trim() || null,
      color: form.color,
      repeat: form.repeat,
      end_date: form.repeat !== 'none' && form.endDate ? dateKey(form.endDate) : null,
      exceptions: null,
      created_at: now,
      updated_at: now,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    closeForm();
    tourBus.emit('krnos:saved');
    await load();
  };

  const updateSingleEvent = async () => {
    if (!form || !form.id || !userId) return;
    setBusy(true);
    const { error: err } = await supabase
      .from('calendar_events')
      .update({
        title: form.title.trim(),
        date: dateKey(form.date),
        start_time: form.startTime,
        end_time: form.endTime,
        notes: form.notes.trim() || null,
        color: form.color,
        repeat: form.repeat,
        end_date: form.repeat !== 'none' && form.endDate ? dateKey(form.endDate) : null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', form.id)
      .eq('user_id', userId);
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    closeForm();
    await load();
  };

  // Editing a single occurrence of a recurring series: spin off a standalone
  // one-off event with the edits, and add this occurrence to the original
  // series' exceptions. Mirrors handleUpdateRecurringEvent('single').
  const saveSingleOccurrenceEdit = async () => {
    if (!form || !form.original || !userId) return;
    setBusy(true);
    const now = new Date().toISOString();
    const occurrenceStr = dateKey(form.date);
    const original = form.original;
    const existingExceptions = original.exceptions ?? [];
    const nextExceptions = existingExceptions.includes(occurrenceStr)
      ? existingExceptions
      : [...existingExceptions, occurrenceStr];

    const [{ error: insertErr }, { error: updateErr }] = await Promise.all([
      supabase.from('calendar_events').insert({
        user_id: userId,
        title: form.title.trim(),
        date: occurrenceStr,
        start_time: form.startTime,
        end_time: form.endTime,
        notes: form.notes.trim() || null,
        color: form.color,
        repeat: 'none',
        end_date: null,
        exceptions: null,
        created_at: now,
        updated_at: now,
      }),
      supabase
        .from('calendar_events')
        .update({ exceptions: nextExceptions, updated_at: now })
        .eq('id', original.id)
        .eq('user_id', userId),
    ]);
    setBusy(false);
    if (insertErr || updateErr) {
      setError((insertErr ?? updateErr)?.message ?? 'Update failed.');
      return;
    }
    closeForm();
    await load();
  };

  // Editing "this and future" occurrences: truncate the old series the day
  // before the edited occurrence, and start a new series with the edits.
  // Mirrors handleUpdateRecurringEvent('future').
  const saveFutureEdit = async () => {
    if (!form || !form.original || !userId) return;
    setBusy(true);
    const now = new Date().toISOString();
    const changeDate = form.date;
    const oldSeriesEnd = dateKey(addDays(changeDate, -1));
    const original = form.original;

    const [{ error: updateErr }, { error: insertErr }] = await Promise.all([
      supabase
        .from('calendar_events')
        .update({ end_date: oldSeriesEnd, updated_at: now })
        .eq('id', original.id)
        .eq('user_id', userId),
      supabase.from('calendar_events').insert({
        user_id: userId,
        title: form.title.trim(),
        date: dateKey(changeDate),
        start_time: form.startTime,
        end_time: form.endTime,
        notes: form.notes.trim() || null,
        color: form.color,
        repeat: form.repeat,
        end_date: form.repeat !== 'none' && form.endDate ? dateKey(form.endDate) : null,
        exceptions: null,
        created_at: now,
        updated_at: now,
      }),
    ]);
    setBusy(false);
    if (updateErr || insertErr) {
      setError((updateErr ?? insertErr)?.message ?? 'Update failed.');
      return;
    }
    closeForm();
    await load();
  };

  const handleSavePress = () => {
    if (!form) return;
    if (!form.title.trim()) {
      Alert.alert('Title required', 'Please enter a title for this event.');
      return;
    }
    const start = normalizeTimeInput(form.startTime);
    const end = normalizeTimeInput(form.endTime);
    if (!start || !end) {
      Alert.alert('Invalid time', 'Enter start/end time as HH:MM (24-hour).');
      return;
    }
    setForm({ ...form, startTime: start, endTime: end });

    if (form.mode === 'add') {
      void insertNewEvent();
      return;
    }
    const originalRepeat = form.original?.repeat;
    if (originalRepeat && originalRepeat !== 'none') {
      Alert.alert('Recurring event', 'Apply these changes to:', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'This event only', onPress: () => void saveSingleOccurrenceEdit() },
        { text: 'This & future events', onPress: () => void saveFutureEdit() },
      ]);
    } else {
      void updateSingleEvent();
    }
  };

  const deleteScoped = async (scope: DeleteScope, item: EventRow, occurrenceDate: string) => {
    if (!userId) return;
    setBusy(true);
    let err: { message: string } | null = null;
    if (scope === 'nonRecurring') {
      const res = await supabase.from('calendar_events').delete().eq('id', item.id).eq('user_id', userId);
      err = res.error;
    } else if (scope === 'single') {
      const existing = item.exceptions ?? [];
      const next = existing.includes(occurrenceDate) ? existing : [...existing, occurrenceDate];
      const res = await supabase
        .from('calendar_events')
        .update({ exceptions: next, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .eq('user_id', userId);
      err = res.error;
    } else {
      if (occurrenceDate === item.date) {
        const res = await supabase.from('calendar_events').delete().eq('id', item.id).eq('user_id', userId);
        err = res.error;
      } else {
        const newEnd = dateKey(addDays(parseDateKey(occurrenceDate), -1));
        const res = await supabase
          .from('calendar_events')
          .update({ end_date: newEnd, updated_at: new Date().toISOString() })
          .eq('id', item.id)
          .eq('user_id', userId);
        err = res.error;
      }
    }
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setDetail(null);
    await load();
  };

  const handleDeletePress = () => {
    if (!detail) return;
    const { item, occurrenceDate } = detail;
    const isRecurring = !!item.repeat && item.repeat !== 'none';
    if (isRecurring) {
      Alert.alert('Delete recurring event', 'What would you like to delete?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'This event only', style: 'destructive', onPress: () => void deleteScoped('single', item, occurrenceDate) },
        { text: 'This & future events', style: 'destructive', onPress: () => void deleteScoped('future', item, occurrenceDate) },
      ]);
    } else {
      Alert.alert('Delete event?', item.title, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => void deleteScoped('nonRecurring', item, occurrenceDate) },
      ]);
    }
  };

  const onBodyHScroll = (x: number) => {
    headerHScroll.current?.scrollTo({ x, animated: false });
  };

  const dayColWidth = view === 'weekly' ? DAY_COL_WIDTH : Math.max(containerWidth - TIME_COL_WIDTH - 24, 220);

  if (authLoading || loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>Please log in to view your schedule.</Text>
      </View>
    );
  }

  return (
    <View style={embedded ? styles.rootEmbedded : styles.root} onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}>
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Atmos />
      </View>
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>Schedule</Text>
          <TourBox id="krnos.add">
            <GoldButton label="+ Add Event" style={styles.addButton} onPress={() => { openAddForm(currentDate); tourBus.emit('krnos:add:open'); }} />
          </TourBox>
          {/* TSK toggle: mirrors web's Schedule-header "TSK" button — hides
              recurring events across all views while active. */}
          <Chip small label="TSK" active={tskMode} onPress={() => setTskMode((v) => !v)} />
          {!embedded && <ScreenHelp help={krnosHelp} style={styles.helpPush} />}
        </View>
        {error ? <Text style={styles.error}>{error}</Text> : null}
        <View style={styles.navRow}>
          <Pressable style={styles.navBtn} onPress={goPrevious}>
            <Text style={styles.navBtnText}>‹</Text>
          </Pressable>
          <Pressable style={styles.todayBtn} onPress={goToday}>
            <Text style={styles.todayBtnText}>Today</Text>
          </Pressable>
          <Pressable style={styles.navBtn} onPress={goNext}>
            <Text style={styles.navBtnText}>›</Text>
          </Pressable>
          <Text style={styles.rangeLabel} numberOfLines={1}>{dateRangeLabel}</Text>
        </View>
        <View style={styles.viewToggleRow}>
          <Chip style={styles.toggleBtn} label="Weekly" active={view === 'weekly'} onPress={() => setView('weekly')} />
          <Chip style={styles.toggleBtn} label="Daily" active={view === 'daily'} onPress={() => setView('daily')} />
          <Chip style={styles.toggleBtn} label="Monthly" active={view === 'monthly'} onPress={() => setView('monthly')} />
        </View>
      </View>

      {view === 'monthly' ? (
        <GlassCard style={styles.monthScroll}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.monthHeaderRow}>
            {DAY_NAMES.map((name) => (
              <View key={name} style={styles.monthHeaderCell}>
                <Text style={styles.monthHeaderText}>{name}</Text>
              </View>
            ))}
          </View>
          <View style={styles.monthGrid}>
            {monthDays.map((day) => {
              const inCurrentMonth = isSameMonthDate(day, currentDate);
              const today = isSameDate(day, new Date());
              const dayEvents = eventsByDay.get(dateKey(day)) ?? [];
              return (
                <Pressable
                  key={dateKey(day)}
                  style={[styles.monthCell, !inCurrentMonth && styles.monthCellDim]}
                  onPress={() => openDayFromMonth(day)}
                >
                  <Text
                    style={[
                      styles.monthCellDate,
                      today && styles.monthCellDateToday,
                      !inCurrentMonth && styles.monthCellDateDim,
                    ]}
                  >
                    {day.getDate()}
                  </Text>
                  <View style={styles.monthChipStack}>
                    {dayEvents.slice(0, 3).map((item) => (
                      <View
                        key={item.id}
                        style={[styles.monthChip, { backgroundColor: resolveColorHex(item.color) + '33', borderLeftColor: resolveColorHex(item.color) }]}
                      >
                        <Text style={styles.monthChipText} numberOfLines={1}>
                          {formatTimeDisplay(item.start_time)} {item.title}
                        </Text>
                      </View>
                    ))}
                    {dayEvents.length > 3 && (
                      <Text style={styles.monthMoreText}>+{dayEvents.length - 3} more</Text>
                    )}
                  </View>
                </Pressable>
              );
            })}
          </View>
        </ScrollView>
        </GlassCard>
      ) : (
      <GlassCard style={styles.gridWrapper}>
        {/* Pinned day-header row, horizontally synced (not directly scrollable) to the body */}
        <View style={styles.headerRow}>
          <View style={[styles.timeHeaderCell, { width: TIME_COL_WIDTH }]}>
            <Text style={styles.timeHeaderText}>Time</Text>
          </View>
          <ScrollView
            ref={headerHScroll}
            horizontal
            scrollEnabled={false}
            showsHorizontalScrollIndicator={false}
            style={styles.hScroll}
          >
            <View style={{ flexDirection: 'row' }}>
              {daysToShow.map((day) => {
                const today = isSameDate(day, new Date());
                return (
                  <View
                    key={dateKey(day)}
                    style={[styles.dayHeaderCell, { width: dayColWidth }, today && styles.dayHeaderCellToday]}
                  >
                    <Text style={[styles.dayHeaderName, today && styles.dayHeaderTextToday]}>
                      {DAY_NAMES[day.getDay()]}
                    </Text>
                    <Text style={[styles.dayHeaderDate, today && styles.dayHeaderTextToday]}>
                      {MONTH_NAMES_SHORT[day.getMonth()]} {day.getDate()}
                    </Text>
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </View>

        {/* Vertically scrollable body — both the pinned time column and the
            horizontally scrollable day columns scroll together. */}
        <ScrollView style={styles.vScroll} showsVerticalScrollIndicator={false}>
          <View style={{ flexDirection: 'row' }}>
            <View style={[styles.timeColumn, { width: TIME_COL_WIDTH, height: gridHeight }]}>
              {timeSlots.map((t) => (
                <View key={t} style={[styles.timeCell, { height: SLOT_HEIGHT }]}>
                  <Text style={styles.timeCellText}>{formatTimeDisplay(t)}</Text>
                </View>
              ))}
            </View>
            <ScrollView
              ref={bodyHScroll}
              horizontal
              showsHorizontalScrollIndicator={false}
              onScroll={(e) => onBodyHScroll(e.nativeEvent.contentOffset.x)}
              scrollEventThrottle={16}
            >
              <View style={{ flexDirection: 'row' }}>
                {daysToShow.map((day) => {
                  const dayEvents = eventsByDay.get(dateKey(day)) ?? [];
                  const overlap = computeOverlapLayout(dayEvents, calendarConfig.interval);
                  // Events that fall entirely outside the configured display
                  // window. Never silently dropped — surfaced via the
                  // "+N outside hours" badge rendered below the grid.
                  const outsideEvents = dayEvents.filter((item) => {
                    const { start, end } = renderRange(item, calendarConfig.interval);
                    const clampedStart = Math.max(start, dayStartMin);
                    const clampedEnd = Math.min(end, dayEndMin);
                    return clampedEnd <= clampedStart;
                  });
                  const today = isSameDate(day, new Date());
                  return (
                    <View
                      key={dateKey(day)}
                      style={[
                        styles.dayColumn,
                        { width: dayColWidth, height: gridHeight },
                        today && styles.dayColumnToday,
                      ]}
                    >
                      {timeSlots.map((t, i) => (
                        <Pressable
                          key={t}
                          style={[styles.slotCell, { top: i * SLOT_HEIGHT, height: SLOT_HEIGHT }]}
                          onPress={() => openAddForm(day, t)}
                        />
                      ))}
                      {dayEvents.map((item) => {
                        const { start, end } = renderRange(item, calendarConfig.interval);
                        const clampedStart = Math.max(start, dayStartMin);
                        const clampedEnd = Math.min(end, dayEndMin);
                        if (clampedEnd <= clampedStart) return null;
                        const top = (clampedStart - dayStartMin) * pxPerMinute;
                        const height = Math.max((clampedEnd - clampedStart) * pxPerMinute - 2, 16);
                        const layout = overlap.get(item.id);
                        const totalCols = layout?.totalColumns ?? 1;
                        const colIndex = layout?.columnIndex ?? 0;
                        const widthPct = 100 / totalCols;
                        const leftPct = colIndex * widthPct;
                        const bg = resolveColorHex(item.color);
                        const recurring = !!item.repeat && item.repeat !== 'none';
                        return (
                          <Pressable
                            key={item.id}
                            style={[
                              styles.eventBlock,
                              {
                                top,
                                height,
                                left: `${leftPct}%` as unknown as number,
                                width: `${widthPct}%` as unknown as number,
                                backgroundColor: bg + '38',
                                borderLeftColor: bg,
                              },
                            ]}
                            onPress={() => setDetail({ item, occurrenceDate: dateKey(day) })}
                          >
                            {recurring && <Text style={styles.repeatGlyph}>⟳ </Text>}
                            <Text style={styles.eventTitle} numberOfLines={height > 32 ? 3 : 1}>
                              {item.title}
                            </Text>
                          </Pressable>
                        );
                      })}
                      {outsideEvents.length > 0 && (
                        <Pressable
                          style={styles.outsideBadge}
                          onPress={() => {
                            if (outsideEvents.length === 1) {
                              setDetail({ item: outsideEvents[0], occurrenceDate: dateKey(day) });
                            } else {
                              Alert.alert(
                                `${outsideEvents.length} events outside hours`,
                                outsideEvents
                                  .map((it) => `${formatTimeDisplay(it.start_time)} – ${formatTimeDisplay(it.end_time)}  ${it.title}`)
                                  .join('\n')
                              );
                            }
                          }}
                        >
                          <Text style={styles.outsideBadgeText} numberOfLines={1}>
                            +{outsideEvents.length} outside hours
                          </Text>
                        </Pressable>
                      )}
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </View>
        </ScrollView>
      </GlassCard>
      )}

      {/* ---------------- Detail modal (view / edit / delete) --------------- */}
      <Modal
        visible={!!detail}
        transparent
        animationType="fade"
        onRequestClose={() => setDetail(null)}
      >
        <View style={styles.backdrop}>
          <GlassCard strong style={styles.detailCard}>
            {detail && (
              <>
                <View style={[styles.colorDot, { backgroundColor: resolveColorHex(detail.item.color) }]} />
                <Text style={styles.detailTitle}>{detail.item.title}</Text>
                <Text style={styles.detailMeta}>
                  {formatShortDate(parseDateKey(detail.occurrenceDate))}
                </Text>
                <Text style={styles.detailMeta}>
                  {formatTimeDisplay(detail.item.start_time)} – {formatTimeDisplay(detail.item.end_time)}
                </Text>
                {repeatLabel(detail.item) && (
                  <Text style={styles.repeatBadge}>{repeatLabel(detail.item)}</Text>
                )}
                {detail.item.notes ? <Text style={styles.detailNotes}>{detail.item.notes}</Text> : null}
                <View style={styles.detailButtonRow}>
                  <GoldButton ghost label="Close" onPress={() => setDetail(null)} />
                  <Pressable
                    style={[styles.footerBtn, styles.footerBtnDanger]}
                    onPress={handleDeletePress}
                    disabled={busy}
                  >
                    <Text style={styles.footerBtnDangerText}>Delete</Text>
                  </Pressable>
                  <GoldButton label="Edit" onPress={() => { if (!busy) openEditForm(detail.item, detail.occurrenceDate); }} />
                </View>
              </>
            )}
          </GlassCard>
        </View>
      </Modal>

      {/* ---------------- Add / Edit form modal ---------------- */}
      <FormShell inline={ob.active} visible={!!form} onRequestClose={closeForm}>
        <KeyboardAvoidingView
          style={styles.backdrop}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <GlassCard strong style={styles.formCard}>
            <Text style={styles.detailTitle}>{form?.mode === 'edit' ? 'Edit Event' : 'Add New Event'}</Text>
            <ScrollView style={styles.formScroll} keyboardShouldPersistTaps="handled">
              <Text style={styles.fieldLabel}>Title</Text>
              <TourBox id="krnos.title">
                <Field
                  placeholder="e.g., Team meeting"
                  value={form?.title ?? ''}
                  onChangeText={(t) => form && setForm({ ...form, title: t })}
                  returnKeyType="done"
                  onBlur={() => { if (form?.title.trim()) tourBus.emit('krnos:title'); }}
                  onSubmitEditing={() => { if (form?.title.trim()) tourBus.emit('krnos:title'); }}
                />
              </TourBox>

              <Text style={styles.fieldLabel}>Date</Text>
              <TourBox id="krnos.day">
              <View style={styles.stepperRow}>
                <Pressable
                  style={styles.stepperBtn}
                  onPress={() => { if (form) { setForm({ ...form, date: addDays(form.date, -1) }); tourBus.emit('krnos:day'); } }}
                >
                  <Text style={styles.stepperBtnText}>‹</Text>
                </Pressable>
                <Text style={styles.stepperValue}>{form ? formatShortDate(form.date) : ''}</Text>
                <Pressable
                  style={styles.stepperBtn}
                  onPress={() => { if (form) { setForm({ ...form, date: addDays(form.date, 1) }); tourBus.emit('krnos:day'); } }}
                >
                  <Text style={styles.stepperBtnText}>›</Text>
                </Pressable>
              </View>
              </TourBox>

              <View style={styles.rowGap}>
                <View style={styles.halfField}>
                  <Text style={styles.fieldLabel}>Start (24h)</Text>
                  <TourBox id="krnos.start">
                    <Field
                      placeholder="09:00"
                      value={form?.startTime ?? ''}
                      onChangeText={(t) => form && setForm({ ...form, startTime: t })}
                      returnKeyType="done"
                      onBlur={() => { if (form && normalizeTimeInput(form.startTime)) tourBus.emit('krnos:start'); }}
                      onSubmitEditing={() => { if (form && normalizeTimeInput(form.startTime)) tourBus.emit('krnos:start'); }}
                    />
                  </TourBox>
                </View>
                <View style={styles.halfField}>
                  <Text style={styles.fieldLabel}>End (24h)</Text>
                  <TourBox id="krnos.end">
                    <Field
                      placeholder="09:30"
                      value={form?.endTime ?? ''}
                      onChangeText={(t) => form && setForm({ ...form, endTime: t })}
                      returnKeyType="done"
                      onBlur={() => { if (form && normalizeTimeInput(form.endTime)) tourBus.emit('krnos:end'); }}
                      onSubmitEditing={() => { if (form && normalizeTimeInput(form.endTime)) tourBus.emit('krnos:end'); }}
                    />
                  </TourBox>
                </View>
              </View>

              <Text style={styles.fieldLabel}>Notes</Text>
              <Field
                inputStyle={styles.notesInput}
                placeholder="Optional notes…"
                value={form?.notes ?? ''}
                onChangeText={(t) => form && setForm({ ...form, notes: t })}
                multiline
                textAlignVertical="top"
              />

              <Text style={styles.fieldLabel}>Color</Text>
              <View style={styles.swatchRow}>
                {COLOR_SWATCHES.map((c) => (
                  <Pressable
                    key={c}
                    style={[
                      styles.swatch,
                      { backgroundColor: c },
                      form?.color === c && styles.swatchSelected,
                    ]}
                    onPress={() => form && setForm({ ...form, color: c })}
                  />
                ))}
              </View>

              <Text style={styles.fieldLabel}>Repeat</Text>
              <View style={styles.chipRow}>
                {REPEAT_OPTIONS.map((opt) => {
                  const chip = (
                    <Chip
                      key={opt}
                      label={opt.charAt(0).toUpperCase() + opt.slice(1)}
                      active={form?.repeat === opt}
                      onPress={() => { if (form) { setForm({ ...form, repeat: opt }); if (opt === 'weekly') tourBus.emit('krnos:repeat:weekly'); } }}
                    />
                  );
                  return opt === 'weekly' ? <TourBox key={opt} id="krnos.repeat.weekly">{chip}</TourBox> : chip;
                })}
              </View>

              {form && form.repeat !== 'none' && (
                <>
                  <Text style={styles.fieldLabel}>Ends</Text>
                  <View style={styles.stepperRow}>
                    <Pressable
                      style={styles.stepperBtn}
                      onPress={() => form && setForm({ ...form, endDate: addDays(form.endDate ?? form.date, -1) })}
                    >
                      <Text style={styles.stepperBtnText}>‹</Text>
                    </Pressable>
                    <Text style={styles.stepperValue}>
                      {form.endDate ? formatShortDate(form.endDate) : 'Never'}
                    </Text>
                    <Pressable
                      style={styles.stepperBtn}
                      onPress={() => form && setForm({ ...form, endDate: addDays(form.endDate ?? form.date, 1) })}
                    >
                      <Text style={styles.stepperBtnText}>›</Text>
                    </Pressable>
                    {form.endDate && (
                      <Pressable style={styles.clearBtn} onPress={() => form && setForm({ ...form, endDate: null })}>
                        <Text style={styles.clearBtnText}>Clear</Text>
                      </Pressable>
                    )}
                  </View>
                </>
              )}
            </ScrollView>

            <View style={styles.detailButtonRow}>
              <GoldButton ghost label="Cancel" onPress={() => { if (!busy) closeForm(); }} />
              <TourBox id="krnos.save" style={{ flex: 1 }}>
              <GoldButton
                style={{ flex: 1 }}
                label={busy ? 'Saving…' : form?.mode === 'edit' ? 'Save Changes' : 'Add Event'}
                onPress={() => { if (!busy) handleSavePress(); }}
              />
              </TourBox>
            </View>
          </GlassCard>
        </KeyboardAvoidingView>
      </FormShell>
    </View>
  );
}

// Thin wrapper: the KRNOS tool is the whole-calendar schedule (no calendar scope).
export function KrnosTool(_props: ToolProps) {
  return <KrnosSchedule />;
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1, padding: 10 },
  // Embedded in a scrolling parent (TKNE): fixed height so the inner vertical
  // ScrollViews have a bounded viewport to scroll within (flex:1 would collapse).
  rootEmbedded: { height: 560, padding: 10 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  emptyText: { color: c.slate, fontSize: 14, textAlign: 'center' },
  error: { color: c.danger, fontSize: 12, marginBottom: 4 },

  header: { marginBottom: 8 },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  title: { fontSize: 20, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
  addButton: { height: 34, paddingHorizontal: 14, ...neu.glowPrimary },
  helpPush: { marginLeft: 'auto' },
  addButtonText: { fontWeight: '900', color: c.blueText, fontSize: 12 },

  navRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  navBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1, borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnText: { fontSize: 16, fontWeight: '900', color: c.blueText },
  todayBtn: {
    borderRadius: 999,
    borderWidth: 1, borderColor: t.navyLine,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  todayBtnText: { fontSize: 12, fontWeight: '700', color: c.text },
  rangeLabel: { flex: 1, fontSize: 13, fontWeight: '700', color: c.text, textAlign: 'right' },

  viewToggleRow: { flexDirection: 'row', gap: 6 },
  toggleBtn: { flex: 1, alignItems: 'center' },

  monthScroll: { flex: 1, padding: 0, borderRadius: 22 },
  monthHeaderRow: { flexDirection: 'row' },
  monthHeaderCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  monthHeaderText: { fontSize: 11, fontWeight: '900', color: c.slate },
  monthGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  monthCell: {
    width: '14.2857%',
    minHeight: 84,
    padding: 4,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderColor: t.lineFaint,
  },
  monthCellDim: { backgroundColor: c.limeSoft, opacity: 0.55 },
  monthCellDate: { fontSize: 12, fontWeight: '700', color: c.text, marginBottom: 2 },
  monthCellDateToday: {
    color: t.onGold,
    backgroundColor: t.gold,
    alignSelf: 'flex-start',
    borderRadius: 9,
    paddingHorizontal: 6,
    overflow: 'hidden',
  },
  monthCellDateDim: { color: c.slate },
  monthChipStack: { gap: 2 },
  monthChip: { borderRadius: 4, paddingHorizontal: 3, paddingVertical: 1, borderLeftWidth: 3 },
  monthChipText: { color: t.ivory, fontSize: 9, fontWeight: '700' },
  monthMoreText: { fontSize: 9, fontWeight: '700', color: c.slate },

  gridWrapper: { flex: 1, padding: 0, borderRadius: 22 },
  headerRow: { flexDirection: 'row' },
  hScroll: { flexGrow: 0 },
  timeHeaderCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: t.navyLine,
  },
  timeHeaderText: { fontSize: 11, fontWeight: '900', color: c.slate },
  dayHeaderCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: t.navyLine,
    borderLeftWidth: 1,
    borderLeftColor: t.lineFaint,
  },
  dayHeaderCellToday: { backgroundColor: c.lime },
  dayHeaderName: { fontSize: 11, fontWeight: '900', color: c.slate },
  dayHeaderDate: { fontSize: 11, color: c.slate },
  dayHeaderTextToday: { color: c.blueText },

  vScroll: { flex: 1 },
  timeColumn: { backgroundColor: t.navy },
  timeCell: {
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingRight: 6,
  },
  timeCellText: { fontSize: 10, color: c.slate },

  dayColumn: { position: 'relative', borderLeftWidth: 1, borderLeftColor: t.lineFaint },
  dayColumnToday: { backgroundColor: c.lime },
  slotCell: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  eventBlock: {
    position: 'absolute',
    borderRadius: 6,
    borderLeftWidth: 3,
    paddingHorizontal: 4,
    paddingVertical: 2,
    zIndex: 5,
  },
  eventTitle: { color: t.ivory, fontSize: 10, fontWeight: '700', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  repeatGlyph: { color: t.goldBright, fontSize: 10 },
  outsideBadge: {
    position: 'absolute',
    left: 2,
    right: 2,
    bottom: 2,
    borderRadius: radii.pill,
    backgroundColor: t.glassStrong,
    borderWidth: 1,
    borderColor: t.navyLine,
    paddingVertical: 2,
    alignItems: 'center',
    zIndex: 6,
  },
  outsideBadgeText: { color: t.ivory, fontSize: 9, fontWeight: '700' },

  backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
  detailCard: {
    borderRadius: 28,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    padding: 20,
    paddingBottom: 28,
  },
  colorDot: { width: 14, height: 14, borderRadius: 7, marginBottom: 8 },
  detailTitle: { fontSize: 18, color: t.ivory, marginBottom: 6, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
  detailMeta: { fontSize: 13, color: c.slate, marginBottom: 2 },
  detailNotes: { fontSize: 13, color: c.text, marginTop: 8 },
  repeatBadge: {
    marginTop: 6,
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: '700',
    color: t.goldBright,
    backgroundColor: c.lime,
    borderRadius: radii.pill,
    paddingHorizontal: 8,
    paddingVertical: 3,
    overflow: 'hidden',
  },
  detailButtonRow: { flexDirection: 'row', gap: 8, marginTop: 16 },
  footerBtn: {
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerBtnDanger: { borderWidth: 1, borderColor: t.lapsed, borderRadius: 22, height: 44, paddingVertical: 0 },
  footerBtnDangerText: { fontWeight: '700', color: c.danger, fontSize: 13 },

  formCard: {
    borderRadius: 28,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    padding: 20,
    paddingBottom: 24,
    maxHeight: '88%',
  },
  formScroll: { marginTop: 4 },
  fieldLabel: {
    fontSize: 10.5,
    fontWeight: '800',
    color: t.ivoryFaint,
    textTransform: 'uppercase',
    letterSpacing: 1.6,
    marginTop: 12,
    marginBottom: 4,
  },
  notesInput: { minHeight: 70, textAlignVertical: 'top' },
  rowGap: { flexDirection: 'row', gap: 10 },
  halfField: { flex: 1 },
  stepperRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  stepperBtn: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1, borderColor: t.navyLine,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepperBtnText: { fontSize: 16, fontWeight: '900', color: c.blueText },
  stepperValue: { flex: 1, fontSize: 13, fontWeight: '700', color: c.text, textAlign: 'center' },
  clearBtn: { paddingHorizontal: 8, paddingVertical: 4 },
  clearBtnText: { fontSize: 12, color: c.danger, fontWeight: '700' },
  swatchRow: { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
  swatch: { width: 28, height: 28, borderRadius: 14 },
  swatchSelected: { borderWidth: 3, borderColor: t.ivory },
  chipRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  }),
);
